import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FiArrowRight,
  FiArrowLeft,
  FiSettings,
  FiDatabase,
  FiShield,
  FiCheck,
  FiCpu,
  FiFileText,
  FiMail,
  FiPlus,
  FiTrash2,
  FiSun,
  FiMoon,
  FiType,
  FiPrinter,
  FiExternalLink,
  FiUserCheck,
  FiSearch,
  FiSave,
  FiCheckSquare,
  FiSquare,
  FiUploadCloud,
  FiEdit,
  FiChevronDown,
  FiUser,
  FiPackage,
  FiClock,
  FiList,
  FiBarChart2
} from "react-icons/fi";
import "./ConfigurationPage.css";
import { appRolesService } from "../services/api/appRolesService";
import { apiClient } from "../services/api/baseApi";
import { useAppRole } from "../context/UserRoleContext";
import { APP_ROLE_CODES } from "../constants/appRoles";
import {
  LaborScopeCodesUploadInline,
  StockInventoryItemsUploadInline,
  EquipmentTimeCardsUploadInline,
} from "./PpMasterExcelUpload";
import ReportsProposalTemplates from "./ReportsProposalTemplates";
import StockInventoryItemsManage from "./StockInventoryItemsManage";
import LaborScopeCodesManage from "./LaborScopeCodesManage";
import EquipmentTimeCardsManage from "./EquipmentTimeCardsManage";
import ProjectsProductionPerformanceDataManage from "./ProjectsProductionPerformanceDataManage";

function ConfigurationPage() {
  const { canAccessConfiguration, role } = useAppRole();
  const isAdmin = role === APP_ROLE_CODES.ADMIN || role === "Admin";
  const location = useLocation();
  const navigate = useNavigate();
  const isStockInventoryManagePage = location.pathname === "/configuration/stock-inventory";
  const isLaborScopeManagePage = location.pathname === "/configuration/labor-scope-codes";
  const isEquipmentManagePage = location.pathname === "/configuration/equipment-time-cards";
  const isPpDataManagePage = location.pathname === "/configuration/projects-production-performance-data";
  const isCatalogManagePage = isStockInventoryManagePage || isLaborScopeManagePage || isEquipmentManagePage || isPpDataManagePage;
  const [currentView, setCurrentView] = useState("dashboard"); // dashboard, salesforce, acumatica, role-access, production-report, reports, appearance, labor-scope-codes, stock-inventory-items, equipment-time-cards, projects-production-performance-data

  useEffect(() => {
    const viewFromState = location.state?.configView;
    if (viewFromState) {
      setCurrentView(viewFromState);
    }
  }, [location.state]);

  // Toast Notification State
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  // Appearance (Theme Selection) State
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "light");

  // Sync theme with document class and localStorage, listening for changes
  useEffect(() => {
    const applyTheme = () => {
      const resolved = theme === "system"
        ? (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light")
        : theme;

      if (resolved === "dark") {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
    };

    applyTheme();
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);

    if (theme === "system") {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handler = () => applyTheme();
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener("change", handler);
      } else {
        mediaQuery.addListener(handler);
      }
      return () => {
        if (mediaQuery.removeEventListener) {
          mediaQuery.removeEventListener("change", handler);
        } else {
          mediaQuery.removeListener(handler);
        }
      };
    }
  }, [theme]);

  // Font Selection State
  const [font, setFont] = useState(() => localStorage.getItem("font") || "google-sans-flex");

  // Sync font with document attribute and localStorage
  useEffect(() => {
    document.documentElement.setAttribute("data-font", font);
    localStorage.setItem("font", font);
  }, [font]);

  // Accent Color Selection State
  const [accent, setAccent] = useState(() => localStorage.getItem("accent") || "red");

  // Sync accent with document attribute and localStorage
  useEffect(() => {
    document.documentElement.setAttribute("data-accent", accent);
    localStorage.setItem("accent", accent);
  }, [accent]);

  // Role Access State
  const [emailInput, setEmailInput] = useState("");
  const [roleInput, setRoleInput] = useState("Admin");
  const [availableRoles, setAvailableRoles] = useState([]);
  const [userList, setUserList] = useState([]);
  const [isRolesLoading, setIsRolesLoading] = useState(false);
  const [isUsersLoading, setIsUsersLoading] = useState(false);
  const [isSubmittingRole, setIsSubmittingRole] = useState(false);
  const [revokingUserId, setRevokingUserId] = useState(null);

  // Contact Search State for Email Input
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [isContactSearchLoading, setIsContactSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const searchDropdownRef = useRef(null);

  // Debounced contact search
  const performContactSearch = useCallback(async (searchTerm) => {
    if (searchTerm.length < 3) {
      setSearchResults([]);
      setShowSearchDropdown(false);
      return;
    }

    setIsContactSearchLoading(true);
    try {
      const response = await apiClient.get(`/salesforce/contacts/search/${encodeURIComponent(searchTerm)}`);
      const data = response.data;
      if (data.records && Array.isArray(data.records)) {
        const filtered = data.records.filter(
          (contact) => contact.Email && contact.Email.toLowerCase().endsWith("@nettlescs.com")
        );
        setSearchResults(filtered);
        setShowSearchDropdown(true);
      } else {
        setSearchResults([]);
        setShowSearchDropdown(true);
      }
    } catch (error) {
      console.error("Error searching contacts:", error);
      setSearchResults([]);
      setShowSearchDropdown(false);
    } finally {
      setIsContactSearchLoading(false);
    }
  }, []);

  const handleEmailInputChange = (e) => {
    const value = e.target.value;
    setEmailInput(value);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const newTimeout = setTimeout(() => {
      if (value.trim().length >= 3) {
        performContactSearch(value.trim());
      } else {
        setSearchResults([]);
        setShowSearchDropdown(false);
      }
    }, 500);

    setSearchTimeout(newTimeout);
  };

  // Close dropdown on click outside
  useEffect(() => {
    if (!showSearchDropdown) return;

    const handleClickOutside = (event) => {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target)) {
        setShowSearchDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showSearchDropdown]);

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Salesforce settings state
  const [sfUrl, setSfUrl] = useState("https://nettlescs.my.salesforce.com");
  const [sfClientId, setSfClientId] = useState("3MVG9l2NDclvZpq4y_xyz123_abc");
  const [sfSyncInterval, setSfSyncInterval] = useState("15");

  // Acumatica settings state
  const [acUrl, setAcUrl] = useState("https://nettlescs.acumatica.com");
  const [acCompanyId, setAcCompanyId] = useState("DevCompany");
  const [acBranch, setAcBranch] = useState("MAIN");

  // Production Report settings state
  const [reportFreq, setReportFreq] = useState("Daily");
  const [reportFormat, setReportFormat] = useState("PDF");
  const [reportEmail, setReportEmail] = useState("reports@nettlescs.com");
  const [mobsMetric, setMobsMetric] = useState(true);
  const [daysMetric, setDaysMetric] = useState(true);
  const [tripsMetric, setTripsMetric] = useState(false);

  // User Access Control State
  const [userAccessList, setUserAccessList] = useState(() => {
    const saved = localStorage.getItem("nettles_user_access_permissions");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) { }
    }
    return [
      {
        id: "usr_1",
        name: "Vinod Kumar",
        email: "vkumar@nettlescs.com",
        role: "Admin",
        permissions: {
          editContact: true,
          deleteContact: true,
          editAccount: true,
          deleteAccount: true,
          uploadFiles: true,
          editProductionRow: true,
          exportData: true,
          deleteProject: true,
          accessConfig: true,
        },
      },
      {
        id: "usr_2",
        name: "Marcus Vance",
        email: "mvance@turnerconst.com",
        role: "User",
        permissions: {
          editContact: true,
          deleteContact: false,
          editAccount: true,
          deleteAccount: false,
          uploadFiles: true,
          editProductionRow: true,
          exportData: true,
          deleteProject: false,
          accessConfig: false,
        },
      },
      {
        id: "usr_3",
        name: "Sarah Jenkins",
        email: "sjenkins@nettlescs.com",
        role: "User",
        permissions: {
          editContact: true,
          deleteContact: false,
          editAccount: false,
          deleteAccount: false,
          uploadFiles: true,
          editProductionRow: false,
          exportData: true,
          deleteProject: false,
          accessConfig: false,
        },
      },
      {
        id: "usr_4",
        name: "Alex Morgan",
        email: "amorgan@nettlescs.com",
        role: "User",
        permissions: {
          editContact: false,
          deleteContact: false,
          editAccount: false,
          deleteAccount: false,
          uploadFiles: true,
          editProductionRow: false,
          exportData: false,
          deleteProject: false,
          accessConfig: false,
        },
      },
    ];
  });

  const [selectedUserEmail, setSelectedUserEmail] = useState("mvance@turnerconst.com");
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [userDropdownSearch, setUserDropdownSearch] = useState("");
  const userDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setIsUserDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getInitials = (name) => {
    if (!name) return "U";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const PERMISSION_OPTIONS = [
    {
      key: "editContact",
      label: "Edit Contact Option",
      desc: "Allow user to edit contact profiles, phone numbers, and addresses",
      category: "Contacts"
    },
    {
      key: "deleteContact",
      label: "Delete Contact Option",
      desc: "Allow user to delete contact records permanently",
      category: "Contacts"
    },
    {
      key: "editAccount",
      label: "Edit Account Option",
      desc: "Allow user to modify company account details and billing attributes",
      category: "Accounts"
    },
    {
      key: "deleteAccount",
      label: "Delete Account Option",
      desc: "Allow user to delete company account profiles and related entries",
      category: "Accounts"
    },
    {
      key: "uploadFiles",
      label: "Upload Files Option",
      desc: "Allow user to upload project attachments, scope PDFs, and files",
      category: "Documents"
    },
    {
      key: "editProductionRow",
      label: "Edit Row in Production Performance Option",
      desc: "Allow user to edit daily production tracking entries, yields, and logs",
      category: "Production"
    },
    {
      key: "exportData",
      label: "Export Data Sheets Option",
      desc: "Allow user to export project dashboards and tables to Excel/CSV",
      category: "Reports"
    },
    {
      key: "deleteProject",
      label: "Delete Project Option",
      desc: "Allow user to delete or archive project records",
      category: "Projects"
    },
    {
      key: "accessConfig",
      label: "Manage System Configuration Option",
      desc: "Allow user to access configuration engine settings and mappings",
      category: "System"
    }
  ];

  const handleTogglePermission = (permissionKey) => {
    setUserAccessList((prev) =>
      prev.map((usr) => {
        if (usr.email === selectedUserEmail) {
          return {
            ...usr,
            permissions: {
              ...usr.permissions,
              [permissionKey]: !usr.permissions[permissionKey],
            },
          };
        }
        return usr;
      })
    );
  };

  const handleSelectAllPermissions = (value) => {
    setUserAccessList((prev) =>
      prev.map((usr) => {
        if (usr.email === selectedUserEmail) {
          const updated = {};
          PERMISSION_OPTIONS.forEach((opt) => {
            updated[opt.key] = value;
          });
          return { ...usr, permissions: updated };
        }
        return usr;
      })
    );
  };

  const handleSaveUserAccessPermissions = () => {
    localStorage.setItem("nettles_user_access_permissions", JSON.stringify(userAccessList));
    const targetUser = userAccessList.find((u) => u.email === selectedUserEmail);
    triggerToast(`User access permissions saved for ${targetUser?.name || selectedUserEmail}`);
  };

  const triggerToast = (msg) => {
    setToastMessage(msg);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const loadRoleAccessData = useCallback(async () => {
    setIsRolesLoading(true);
    setIsUsersLoading(true);

    try {
      const [roles, users] = await Promise.all([
        appRolesService.listRoles(),
        appRolesService.listAssignedUsers(),
      ]);

      setAvailableRoles(roles);
      setUserList(users);

      // Always lock to Admin role
      setRoleInput("Admin");
    } catch (error) {
      console.error("Failed to load role access data:", error);
      triggerToast("Failed to load roles and assigned users");
    } finally {
      setIsRolesLoading(false);
      setIsUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (currentView === "role-access") {
      loadRoleAccessData();
    } else {
      setSearchResults([]);
      setShowSearchDropdown(false);
      setIsContactSearchLoading(false);
      setEmailInput("");
    }
  }, [currentView, loadRoleAccessData]);

  const handleRoleSubmit = async (e) => {
    e.preventDefault();
    if (!emailInput) {
      triggerToast("Please enter an email address");
      return;
    }

    if (!roleInput) {
      triggerToast("Please select a role");
      return;
    }

    setIsSubmittingRole(true);
    try {
      const assignment = await appRolesService.assignUserRole(emailInput, roleInput);
      setUserList((prev) => {
        const withoutExisting = prev.filter((user) => user.email !== assignment.email);
        return [assignment, ...withoutExisting];
      });
      setEmailInput("");
      setSearchResults([]);
      setShowSearchDropdown(false);
      triggerToast(`Access granted: ${assignment.roleDisplayName || assignment.role} role assigned to ${assignment.email}`);
    } catch (error) {
      console.error("Failed to assign user role:", error);
      const message = error?.response?.data?.message || "Failed to assign user role";
      triggerToast(message);
    } finally {
      setIsSubmittingRole(false);
    }
  };

  const handleRemoveUser = async (id, userEmail) => {
    setRevokingUserId(id);
    try {
      await appRolesService.revokeUserRole(id);
      setUserList((prev) => prev.filter((user) => user.id !== id));
      triggerToast(`Revoked access for ${userEmail}`);
    } catch (error) {
      console.error("Failed to revoke user role:", error);
      const message = error?.response?.data?.message || "Failed to revoke user access";
      triggerToast(message);
    } finally {
      setRevokingUserId(null);
    }
  };

  const handleSaveConfig = (type) => {
    triggerToast(`${type} configurations saved successfully`);
  };

  const renderRoleBadge = (role) => {
    if (role === "Admin" || role === "ADMIN") {
      return <span className="badge-role badge-admin">Admin</span>;
    }
    return <span className="badge-role badge-staff" style={{ background: "#3b82f615", color: "#3b82f6", border: "1px solid #3b82f640" }}>User</span>;
  };

  // Grid tiles configuration
  const configTiles = [
    {
      id: "salesforce",
      title: "Salesforce Mappings",
      desc: "Map record types to Salesforce objects and fields",
      icon: <FiDatabase />,
      color: "#3b82f6", // Blue
      adminOnly: false
    },
    {
      id: "acumatica",
      title: "Acumatica Configuration",
      desc: "Configure endpoints, company IDs, and sync rates",
      icon: <FiCpu />,
      color: "#06b6d4", // Cyan
      adminOnly: false
    },
    {
      id: "role-access",
      title: "Role-Based Access Control",
      desc: "Manage user roles, grant permissions, and configure access",
      icon: <FiShield />,
      color: "#10b981", // Green
      adminOnly: true
    },
    {
      id: "user-access-control",
      title: "User Access Control",
      desc: "Configure granular user action rights, edit permissions, and feature controls",
      icon: <FiUserCheck />,
      color: "#6366f1", // Indigo
      adminOnly: true
    },
    {
      id: "production-report",
      title: "Production Report Settings",
      desc: "Configure templates, schedules, and email distribution list",
      icon: <FiFileText />,
      color: "#f59e0b", // Amber
      adminOnly: false
    },
    {
      id: "reports",
      title: "Reports & Proposal Templates",
      desc: "Build, validate, and manage document proposal templates & reports studio",
      icon: <FiPrinter />,
      color: "#e11d48", // Rose
      adminOnly: true // Admin-only option
    },
    {
      id: "appearance",
      title: "Appearance Settings",
      desc: "Customize the theme and interface style of the application",
      icon: <FiSun />,
      color: "#8b5cf6", // Purple
      adminOnly: false
    },
    {
      id: "labor-scope-codes",
      title: "Upload Labor & Scope Codes",
      desc: "Upload the Labor & Scope Codes master Excel sheet used across production entry",
      icon: <FiUploadCloud />,
      color: "#15803d", // Green
      adminOnly: false
    },
    {
      id: "stock-inventory-items",
      title: "Upload Stock Inventory Items",
      desc: "Upload the Stock Inventory Items master Excel sheet for inventory tracking",
      icon: <FiPackage />,
      color: "#7c3aed", // Violet
      adminOnly: false
    },
    {
      id: "equipment-time-cards",
      title: "Upload Equipment Time Cards",
      desc: "Upload the Equipment Time Cards master Excel sheet for equipment tracking",
      icon: <FiClock />,
      color: "#0f766e", // Teal
      adminOnly: false
    },
    {
      id: "projects-production-performance-data",
      title: "Manage Projects Production Performance Data",
      desc: "Select a project to view, edit, upload, download, or delete its Production Performance Excel data",
      icon: <FiBarChart2 />,
      color: "#be123c", // Rose / brand red
      adminOnly: false
    }
  ];

  return (
    <div className={`config-engine-wrapper ${theme === 'dark' ? 'dark' : ''} ${isCatalogManagePage ? 'config-engine-wrapper--stock-inv' : ''}`}>
      {/* Toast popup */}
      <div className={`toast-notification ${showToast ? "show" : ""}`}>
        <div className="toast-content">
          <FiCheck className="toast-icon" />
          <span>{toastMessage}</span>
        </div>
      </div>

      {isStockInventoryManagePage ? (
        canAccessConfiguration ? (
          <StockInventoryItemsManage
            onBack={() => navigate("/configuration", { state: { configView: "stock-inventory-items" } })}
            onToast={triggerToast}
          />
        ) : null
      ) : isLaborScopeManagePage ? (
        canAccessConfiguration ? (
          <LaborScopeCodesManage
            onBack={() => navigate("/configuration", { state: { configView: "labor-scope-codes" } })}
            onToast={triggerToast}
          />
        ) : null
      ) : isEquipmentManagePage ? (
        canAccessConfiguration ? (
          <EquipmentTimeCardsManage
            onBack={() => navigate("/configuration", { state: { configView: "equipment-time-cards" } })}
            onToast={triggerToast}
          />
        ) : null
      ) : isPpDataManagePage ? (
        canAccessConfiguration ? (
          <ProjectsProductionPerformanceDataManage
            onBack={() => navigate("/configuration")}
            onToast={triggerToast}
          />
        ) : null
      ) : (
        <>
      {currentView === "dashboard" && (
        <div className="config-dashboard-view">
          <header className="engine-header">
            <h1>Configuration Engine</h1>
            <p>Configure workflows, system mappings, access rules, and proposal templates for Nettles.</p>
          </header>

          <div className="engine-grid">
            {configTiles
              .filter((tile) => tile.id === "appearance" || (tile.adminOnly ? isAdmin : canAccessConfiguration))
              .map((tile) => (
                <div
                  key={tile.id}
                  className="engine-card"
                  onClick={() => {
                    if (tile.id === "projects-production-performance-data") {
                      navigate("/configuration/projects-production-performance-data");
                      return;
                    }
                    if (["salesforce", "acumatica", "role-access", "user-access-control", "production-report", "reports", "appearance", "labor-scope-codes", "stock-inventory-items", "equipment-time-cards"].includes(tile.id)) {
                      if (tile.adminOnly && !isAdmin) {
                        triggerToast("Access restricted: This panel is only available for Admins.");
                        return;
                      }
                      setCurrentView(tile.id);
                    } else {
                      triggerToast(`"${tile.title}" configuration panel is coming soon!`);
                    }
                  }}
                >
                  <div className="engine-card-left">
                    <div
                      className="card-icon-container"
                      style={{ backgroundColor: `${tile.color}15`, color: tile.color }}
                    >
                      {tile.icon}
                    </div>
                    <div className="card-text-container">
                      <h3>{tile.title}</h3>
                      <p>{tile.desc}</p>
                    </div>
                  </div>
                  <div className="engine-card-arrow">
                    <FiArrowRight />
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Role Based Access Control View */}
      {currentView === "role-access" && canAccessConfiguration && (
        <div className="config-sub-view fade-in">
          <div className="sub-view-nav" onClick={() => setCurrentView("dashboard")}>
            <FiArrowLeft /> Back to Configuration Engine
          </div>

          <header className="sub-view-header">
            <div className="header-icon-badge" style={{ backgroundColor: "#10b98115", color: "#10b981" }}>
              <FiShield />
            </div>
            <div>
              <h2>Role-Based Access Control</h2>
              <p>Grant access to users and manage permissions for different operational roles.</p>
            </div>
          </header>

          <div className="sub-view-content-grid">
            {/* Form */}
            <div className="sub-view-card">
              <h3>Grant User Access</h3>
              <form onSubmit={handleRoleSubmit} className="role-grant-form">
                <div className="form-group">
                  <label>User Email Address</label>
                  <div ref={searchDropdownRef} className="contact-search-dropdown-wrapper">
                    <div className="input-with-icon">
                      <FiMail className="input-inner-icon" />
                      <input
                        type="email"
                        placeholder="e.g. employee@nettlescs.com"
                        value={emailInput}
                        onChange={handleEmailInputChange}
                        required
                        autoComplete="off"
                      />
                      {isContactSearchLoading && <span className="contact-search-spinner"></span>}
                    </div>

                    {showSearchDropdown && (
                      <div className="contact-search-dropdown">
                        {searchResults.length > 0 ? (
                          <ul className="contact-search-dropdown-list">
                            {searchResults.map((contact) => (
                              <li
                                key={contact.Id}
                                className="contact-search-dropdown-item"
                                onClick={() => {
                                  setEmailInput(contact.Email || "");
                                  setShowSearchDropdown(false);
                                }}
                              >
                                <span className="contact-search-item-name">{contact.Name}</span>
                                <div className="contact-search-item-meta">
                                  {contact.Email && (
                                    <span className="contact-search-item-email">{contact.Email}</span>
                                  )}
                                  {contact.Account?.Name && (
                                    <span className="contact-search-item-account">• {contact.Account.Name}</span>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div className="contact-search-no-results">No contacts found</div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Assigned Role</label>
                  <input
                    type="text"
                    value="Admin"
                    disabled
                    style={{ cursor: "not-allowed" }}
                  />
                  <span className="form-desc">
                    Controls visibility of financial performance metrics, inventory logs, and project archiving actions.
                  </span>
                </div>

                <button type="submit" className="btn-action" disabled={isSubmittingRole || isRolesLoading}>
                  <FiPlus /> {isSubmittingRole ? "Granting..." : "Grant Access Permissions"}
                </button>
              </form>
            </div>

            {/* List */}
            <div className="sub-view-card">
              <h3>Granted Users & Permissions</h3>
              <div className="users-table-wrapper">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Role</th>
                      <th style={{ textAlign: "right" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isUsersLoading ? (
                      <tr>
                        <td colSpan={3} className="users-table-empty">
                          Loading assigned users...
                        </td>
                      </tr>
                    ) : userList.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="users-table-empty">
                          No users have been assigned a role yet.
                        </td>
                      </tr>
                    ) : (
                      userList.map((user) => (
                        <tr key={user.id}>
                          <td>
                            <div className="user-email-cell">
                              <span className="user-dot"></span>
                              {user.email}
                            </div>
                          </td>
                          <td>{renderRoleBadge(user.role)}</td>
                          <td style={{ textAlign: "right" }}>
                            <button
                              type="button"
                              className="btn-revoke"
                              onClick={() => handleRemoveUser(user.id, user.email)}
                              title="Revoke access"
                              disabled={revokingUserId === user.id}
                            >
                              <FiTrash2 />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Access Control & Action Permissions View (Admin Only) */}
      {currentView === "user-access-control" && (
        isAdmin ? (
          <div className="config-sub-view fade-in">
            <div className="sub-view-nav" onClick={() => setCurrentView("dashboard")}>
              <FiArrowLeft /> Back to Configuration Engine
            </div>

            <header className="sub-view-header">
              <div className="header-icon-badge" style={{ backgroundColor: "#6366f115", color: "#6366f1" }}>
                <FiUserCheck />
              </div>
              <div style={{ flex: 1, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem" }}>
                <div>
                  <h2>User Access Control & Action Rights</h2>
                  <p>Configure granular action options, edit/delete privileges, and feature rights per user.</p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <button
                    type="button"
                    className="btn-action"
                    style={{ backgroundColor: "transparent", color: "var(--config-heading)", border: "1px solid var(--config-card-border)" }}
                    onClick={() => handleSelectAllPermissions(true)}
                  >
                    <FiCheckSquare /> Select All
                  </button>
                  <button
                    type="button"
                    className="btn-action"
                    style={{ backgroundColor: "transparent", color: "var(--config-heading)", border: "1px solid var(--config-card-border)" }}
                    onClick={() => handleSelectAllPermissions(false)}
                  >
                    <FiSquare /> Deselect All
                  </button>
                  <button
                    type="button"
                    className="btn-action"
                    onClick={handleSaveUserAccessPermissions}
                  >
                    <FiSave /> Save Permissions
                  </button>
                </div>
              </div>
            </header>

            {/* Custom Premium User Selector Bar */}
            <div className="sub-view-card user-picker-card" style={{ marginBottom: "1.5rem" }} ref={userDropdownRef}>
              <label className="user-picker-label">
                Select User to Configure Access & Action Rights
              </label>

              {(() => {
                const currUser = userAccessList.find((u) => u.email === selectedUserEmail) || userAccessList[1];
                const nonAdminUsers = userAccessList.filter((usr) => usr.role !== "Admin" && usr.role !== "ADMIN");
                const filteredUsers = nonAdminUsers.filter(
                  (usr) =>
                    usr.name.toLowerCase().includes(userDropdownSearch.toLowerCase()) ||
                    usr.email.toLowerCase().includes(userDropdownSearch.toLowerCase())
                );

                return (
                  <div className="custom-user-select-container">
                    {/* Trigger Box */}
                    <div
                      className={`custom-user-select-trigger ${isUserDropdownOpen ? "open" : ""}`}
                      onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                    >
                      <div className="trigger-user-info">
                        <div className="user-avatar-circle">
                          {getInitials(currUser.name)}
                        </div>
                        <div className="user-text-meta">
                          <div className="user-name-line">
                            <strong>{currUser.name}</strong>
                            <span className="user-role-capsule">User</span>
                          </div>
                          <span className="user-email-line">{currUser.email}</span>
                        </div>
                      </div>
                      <div className="trigger-chevron">
                        <FiChevronDown className={`chevron-icon ${isUserDropdownOpen ? "rotated" : ""}`} />
                      </div>
                    </div>

                    {/* Popover Dropdown Menu */}
                    {isUserDropdownOpen && (
                      <div className="custom-user-dropdown-popover">
                        <div className="dropdown-search-box">
                          <FiSearch className="search-box-icon" />
                          <input
                            type="text"
                            placeholder="Search user by name or email..."
                            value={userDropdownSearch}
                            onChange={(e) => setUserDropdownSearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                          />
                        </div>

                        <div className="dropdown-options-list">
                          {filteredUsers.length === 0 ? (
                            <div className="dropdown-no-results">No matching users found</div>
                          ) : (
                            filteredUsers.map((usr) => {
                              const isSelected = usr.email === selectedUserEmail;
                              return (
                                <div
                                  key={usr.id}
                                  className={`dropdown-option-item ${isSelected ? "selected" : ""}`}
                                  onClick={() => {
                                    setSelectedUserEmail(usr.email);
                                    setIsUserDropdownOpen(false);
                                    setUserDropdownSearch("");
                                  }}
                                >
                                  <div className="option-item-left">
                                    <div className="user-avatar-sm">
                                      {getInitials(usr.name)}
                                    </div>
                                    <div className="option-user-details">
                                      <span className="option-user-name">{usr.name}</span>
                                      <span className="option-user-email">{usr.email}</span>
                                    </div>
                                  </div>
                                  {isSelected && <FiCheck className="option-check-icon" />}
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Granular Options Checkbox Grid */}
            <div className="sub-view-card">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
                <h3>Action Rights & Tick Mark Options</h3>
                <span style={{ fontSize: "0.85rem", color: "var(--config-subtext)" }}>
                  Click items below to toggle permissions
                </span>
              </div>

              {(() => {
                const currUser = userAccessList.find((u) => u.email === selectedUserEmail);
                const userPerms = currUser?.permissions || {};

                return (
                  <div className="permissions-checkbox-grid">
                    {PERMISSION_OPTIONS.map((opt) => {
                      const isChecked = Boolean(userPerms[opt.key]);
                      return (
                        <div
                          key={opt.key}
                          className={`permission-checkbox-card ${isChecked ? "checked" : ""}`}
                          onClick={() => handleTogglePermission(opt.key)}
                        >
                          <div className="perm-checkbox-left">
                            <div className="perm-checkbox-icon">
                              {isChecked ? <FiCheckSquare style={{ color: "#10b981", fontSize: "1.25rem" }} /> : <FiSquare style={{ color: "#9ca3af", fontSize: "1.25rem" }} />}
                            </div>
                            <div className="perm-card-text">
                              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                                <h4>{opt.label}</h4>
                                <span className="perm-cat-tag">{opt.category}</span>
                              </div>
                              <p>{opt.desc}</p>
                            </div>
                          </div>
                          <div className="perm-status-badge">
                            {isChecked ? (
                              <span className="badge-role badge-admin" style={{ background: "#10b98115", color: "#10b981", border: "1px solid #10b98140" }}>
                                <FiCheck style={{ marginRight: 4 }} /> Allowed
                              </span>
                            ) : (
                              <span className="badge-role" style={{ background: "#ef444415", color: "#ef4444", border: "1px solid #ef444440" }}>
                                Restricted
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              <div style={{ marginTop: "1.75rem", display: "flex", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="btn-action"
                  onClick={handleSaveUserAccessPermissions}
                >
                  <FiSave /> Save User Access Settings
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="config-sub-view fade-in">
            <div className="sub-view-nav" onClick={() => setCurrentView("dashboard")}>
              <FiArrowLeft /> Back to Configuration Engine
            </div>
            <div className="sub-view-card" style={{ textAlign: "center", padding: "3rem 1.5rem" }}>
              <FiShield style={{ fontSize: "3rem", color: "#dc2626", marginBottom: "1rem" }} />
              <h3>Access Denied</h3>
              <p style={{ color: "var(--config-subtext)", maxWidth: "500px", margin: "0 auto" }}>
                The User Access Control panel is restricted to Admin users only.
              </p>
            </div>
          </div>
        )
      )}

      {/* Salesforce Configuration View */}
      {currentView === "salesforce" && canAccessConfiguration && (
        <div className="config-sub-view fade-in">
          <div className="sub-view-nav" onClick={() => setCurrentView("dashboard")}>
            <FiArrowLeft /> Back to Configuration Engine
          </div>

          <header className="sub-view-header">
            <div className="header-icon-badge" style={{ backgroundColor: "#3b82f615", color: "#3b82f6" }}>
              <FiDatabase />
            </div>
            <div>
              <h2>Salesforce Integration Mappings</h2>
              <p>Configure mapping fields, sandbox/production endpoints, and sync behavior.</p>
            </div>
          </header>

          <div className="sub-view-card max-w-2xl">
            <h3>Salesforce API Preferences</h3>
            <div className="role-grant-form">
              <div className="form-group">
                <label>Salesforce Instance URL</label>
                <input
                  type="url"
                  value={sfUrl}
                  onChange={(e) => setSfUrl(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Connected App Client ID</label>
                <input
                  type="text"
                  value={sfClientId}
                  onChange={(e) => setSfClientId(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Sync Polling Interval (Minutes)</label>
                <select value={sfSyncInterval} onChange={(e) => setSfSyncInterval(e.target.value)}>
                  <option value="5">5 Minutes (Real-time)</option>
                  <option value="15">15 Minutes (Default)</option>
                  <option value="60">Hourly</option>
                </select>
              </div>

              <div className="form-actions-row">
                <button
                  type="button"
                  className="btn-action"
                  onClick={() => handleSaveConfig("Salesforce")}
                >
                  Save Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Acumatica Configuration View */}
      {currentView === "acumatica" && canAccessConfiguration && (
        <div className="config-sub-view fade-in">
          <div className="sub-view-nav" onClick={() => setCurrentView("dashboard")}>
            <FiArrowLeft /> Back to Configuration Engine
          </div>

          <header className="sub-view-header">
            <div className="header-icon-badge" style={{ backgroundColor: "#06b6d415", color: "#06b6d4" }}>
              <FiCpu />
            </div>
            <div>
              <h2>Acumatica ERP Configurations</h2>
              <p>Configure project integration endpoints, company references, and branch codes.</p>
            </div>
          </header>

          <div className="sub-view-card max-w-2xl">
            <h3>Acumatica Sync Preferences</h3>
            <div className="role-grant-form">
              <div className="form-group">
                <label>ERP Base URL</label>
                <input
                  type="url"
                  value={acUrl}
                  onChange={(e) => setAcUrl(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Acumatica Company ID</label>
                <input
                  type="text"
                  value={acCompanyId}
                  onChange={(e) => setAcCompanyId(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label>Default Branch ID</label>
                <input
                  type="text"
                  value={acBranch}
                  onChange={(e) => setAcBranch(e.target.value)}
                />
              </div>

              <div className="form-actions-row">
                <button
                  type="button"
                  className="btn-action"
                  onClick={() => handleSaveConfig("Acumatica")}
                >
                  Save Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Production Report Configuration View */}
      {currentView === "production-report" && canAccessConfiguration && (
        <div className="config-sub-view fade-in">
          <div className="sub-view-nav" onClick={() => setCurrentView("dashboard")}>
            <FiArrowLeft /> Back to Configuration Engine
          </div>

          <header className="sub-view-header">
            <div className="header-icon-badge" style={{ backgroundColor: "#f59e0b15", color: "#f59e0b" }}>
              <FiFileText />
            </div>
            <div>
              <h2>Production Report Configuration</h2>
              <p>Configure templates, export formats, scheduling, and distribution lists for automated reports.</p>
            </div>
          </header>

          <div className="sub-view-card max-w-2xl">
            <h3>Report Preferences</h3>
            <div className="role-grant-form">
              <div className="form-group">
                <label>Export Schedule Frequency</label>
                <select value={reportFreq} onChange={(e) => setReportFreq(e.target.value)}>
                  <option value="Daily">Daily (Every morning at 6:00 AM)</option>
                  <option value="Weekly">Weekly (Every Monday at 6:00 AM)</option>
                  <option value="Monthly">Monthly (1st of every month)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Export Document Format</label>
                <select value={reportFormat} onChange={(e) => setReportFormat(e.target.value)}>
                  <option value="PDF">PDF Document (Formatted Printout)</option>
                  <option value="Excel">Excel Sheets (Raw Data)</option>
                  <option value="CSV">Comma Separated Values (.csv)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Distribution Email</label>
                <input
                  type="email"
                  value={reportEmail}
                  onChange={(e) => setReportEmail(e.target.value)}
                  placeholder="reports@nettlescs.com"
                />
              </div>

              <div className="form-group">
                <label>Report Metrics to Include</label>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", marginTop: "0.25rem" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", textTransform: "none", fontSize: "0.95rem", color: "#374151" }}>
                    <input type="checkbox" checked={mobsMetric} onChange={(e) => setMobsMetric(e.target.checked)} style={{ width: "16px", height: "16px" }} />
                    Include Mobilization Metrics (Mobs Used / Contracted)
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", textTransform: "none", fontSize: "0.95rem", color: "#374151" }}>
                    <input type="checkbox" checked={daysMetric} onChange={(e) => setDaysMetric(e.target.checked)} style={{ width: "16px", height: "16px" }} />
                    Include Day Metrics (Days Used / Estimated)
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", textTransform: "none", fontSize: "0.95rem", color: "#374151" }}>
                    <input type="checkbox" checked={tripsMetric} onChange={(e) => setTripsMetric(e.target.checked)} style={{ width: "16px", height: "16px" }} />
                    Include Trip Metrics (Trips Used / Estimated)
                  </label>
                </div>
              </div>

              <div className="form-actions-row">
                <button
                  type="button"
                  className="btn-action"
                  onClick={() => handleSaveConfig("Production Report")}
                >
                  Save Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reports & Proposal Template Studio View (Admin Only) */}
      {currentView === "reports" && (
        isAdmin ? (
          <div className="config-sub-view fade-in">
            <div className="sub-view-nav" onClick={() => setCurrentView("dashboard")}>
              <FiArrowLeft /> Back to Configuration Engine
            </div>

            <ReportsProposalTemplates />
          </div>
        ) : (
          <div className="config-sub-view fade-in">
            <div className="sub-view-nav" onClick={() => setCurrentView("dashboard")}>
              <FiArrowLeft /> Back to Configuration Engine
            </div>
            <div className="sub-view-card" style={{ textAlign: "center", padding: "3rem 1.5rem" }}>
              <FiShield style={{ fontSize: "3rem", color: "#dc2626", marginBottom: "1rem" }} />
              <h3>Access Denied</h3>
              <p style={{ color: "var(--config-subtext)", maxWidth: "500px", margin: "0 auto" }}>
                The Reports & Proposal Template Studio is restricted to Admin users only.
              </p>
            </div>
          </div>
        )
      )}

      {/* Upload Labor & Scope Codes Configuration View */}
      {currentView === "labor-scope-codes" && canAccessConfiguration && (
        <div className="config-sub-view fade-in">
          <div className="sub-view-nav" onClick={() => setCurrentView("dashboard")}>
            <FiArrowLeft /> Back to Configuration Engine
          </div>

          <header className="sub-view-header" style={{ alignItems: "flex-start" }}>
            <div className="header-icon-badge" style={{ backgroundColor: "#15803d15", color: "#15803d" }}>
              <FiUploadCloud />
            </div>
            <div style={{ flex: 1 }}>
              <h2>Upload Labor &amp; Scope Codes</h2>
              <p>Upload an Excel sheet whose columns match Manage Labor &amp; Scope Codes exactly, including renamed and added fields.</p>
            </div>
            <button
              type="button"
              className="btn-action"
              style={{ backgroundColor: "#15803d", boxShadow: "0 4px 12px rgba(21, 128, 61, 0.25)", whiteSpace: "nowrap" }}
              onClick={() => navigate("/configuration/labor-scope-codes")}
            >
              <FiList /> Manage Labor &amp; Scope Codes
            </button>
          </header>

          <div className="sub-view-card max-w-2xl">
            <h3>Excel Master Sheet</h3>
            <LaborScopeCodesUploadInline />
          </div>
        </div>
      )}

      {/* Upload Stock Inventory Items Configuration View */}
      {currentView === "stock-inventory-items" && canAccessConfiguration && (
        <div className="config-sub-view fade-in">
          <div className="sub-view-nav" onClick={() => setCurrentView("dashboard")}>
            <FiArrowLeft /> Back to Configuration Engine
          </div>

          <header className="sub-view-header" style={{ alignItems: "flex-start" }}>
            <div className="header-icon-badge" style={{ backgroundColor: "#7c3aed15", color: "#7c3aed" }}>
              <FiPackage />
            </div>
            <div style={{ flex: 1 }}>
              <h2>Upload Stock Inventory Items</h2>
              <p>Upload an Excel sheet whose columns match Manage Stock Inventory Items exactly, including renamed and added fields.</p>
            </div>
            <button
              type="button"
              className="btn-action"
              style={{ backgroundColor: "#7c3aed", boxShadow: "0 4px 12px rgba(124, 58, 237, 0.25)", whiteSpace: "nowrap" }}
              onClick={() => navigate("/configuration/stock-inventory")}
            >
              <FiList /> Manage Stock Inventory Items
            </button>
          </header>

          <div className="sub-view-card max-w-2xl">
            <h3>Excel Master Sheet</h3>
            <StockInventoryItemsUploadInline />
          </div>
        </div>
      )}

      {/* Upload Equipment Time Cards Configuration View */}
      {currentView === "equipment-time-cards" && canAccessConfiguration && (
        <div className="config-sub-view fade-in">
          <div className="sub-view-nav" onClick={() => setCurrentView("dashboard")}>
            <FiArrowLeft /> Back to Configuration Engine
          </div>

          <header className="sub-view-header" style={{ alignItems: "flex-start" }}>
            <div className="header-icon-badge" style={{ backgroundColor: "#0f766e15", color: "#0f766e" }}>
              <FiClock />
            </div>
            <div style={{ flex: 1 }}>
              <h2>Upload Equipment Time Cards</h2>
              <p>Upload an Excel sheet whose columns match Manage Equipment Time Cards exactly, including renamed and added fields.</p>
            </div>
            <button
              type="button"
              className="btn-action"
              style={{ backgroundColor: "#0f766e", boxShadow: "0 4px 12px rgba(15, 118, 110, 0.25)", whiteSpace: "nowrap" }}
              onClick={() => navigate("/configuration/equipment-time-cards")}
            >
              <FiList /> Manage Equipment Time Cards
            </button>
          </header>

          <div className="sub-view-card max-w-2xl">
            <h3>Excel Master Sheet</h3>
            <EquipmentTimeCardsUploadInline />
          </div>
        </div>
      )}

      {/* Appearance Settings Configuration View */}
      {currentView === "appearance" && (
        <div className="config-sub-view fade-in">
          <div className="sub-view-nav" onClick={() => setCurrentView("dashboard")}>
            <FiArrowLeft /> Back to Configuration Engine
          </div>

          <div className="customizer-hub-wrapper">
            <header className="customizer-hub-header">
              <h2>Appearance Customizer</h2>
              <p>Configure a personalized environment for your workflows. Changes apply instantly across all workspaces.</p>
            </header>

            {/* Section 1: Workspace Theme Cards with Abstract SVGs */}
            <div className="customizer-hub-section">
              <span className="section-meta-tag">01 / Environment</span>
              <h3>Workspace Theme</h3>
              <div className="theme-deck-row">
                {/* Light Theme */}
                <div
                  className={`theme-deck-card ${theme === "light" ? "active" : ""}`}
                  onClick={() => {
                    setTheme("light");
                    triggerToast("Theme changed to Light Mode");
                  }}
                >
                  <div className="deck-card-visual">
                    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="deck-svg">
                      <circle cx="32" cy="32" r="12" stroke="currentColor" strokeWidth="2.5" strokeDasharray="4 4" className="spinning-sun-ring" />
                      <circle cx="32" cy="32" r="8" fill="currentColor" />
                      <line x1="32" y1="8" x2="32" y2="14" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                      <line x1="32" y1="50" x2="32" y2="56" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                      <line x1="8" y1="32" x2="14" y2="32" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                      <line x1="50" y1="32" x2="56" y2="32" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                    </svg>
                  </div>
                  <span className="deck-card-label">Light Mode</span>
                </div>

                {/* Dark Theme */}
                <div
                  className={`theme-deck-card ${theme === "dark" ? "active" : ""}`}
                  onClick={() => {
                    setTheme("dark");
                    triggerToast("Theme changed to Dark Mode");
                  }}
                >
                  <div className="deck-card-visual">
                    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="deck-svg">
                      <path d="M42 38C42 45.732 35.732 52 28 52C23.5134 52 19.5694 49.8892 17.0601 46.6192C16.1438 45.4251 16.9248 43.6841 18.4206 43.8344C27.9157 44.7891 36.2109 37.4909 36.2109 27.7891C36.2109 22.0911 32.969 17.0504 28.1656 14.562C26.7416 13.8242 27.2995 11.6493 28.8741 11.968C36.2759 13.4682 42 19.9892 42 27.7891C42 27.8594 41.9997 27.9296 41.9991 27.9997C42 28.0001 42 28.0001 42 28.0001V38Z" fill="currentColor" />
                      <circle cx="48" cy="18" r="2" fill="currentColor" className="floating-star" />
                      <circle cx="52" cy="30" r="1.5" fill="currentColor" className="floating-star-delayed" />
                    </svg>
                  </div>
                  <span className="deck-card-label">Dark Mode</span>
                </div>

                {/* System Theme */}
                <div
                  className={`theme-deck-card ${theme === "system" ? "active" : ""}`}
                  onClick={() => {
                    setTheme("system");
                    triggerToast("Theme changed to System Preference");
                  }}
                >
                  <div className="deck-card-visual">
                    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className="deck-svg">
                      <rect x="12" y="14" width="40" height="28" rx="4" stroke="currentColor" strokeWidth="3" />
                      <line x1="20" y1="48" x2="44" y2="48" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                      <line x1="32" y1="42" x2="32" y2="48" stroke="currentColor" strokeWidth="3" />
                      <path d="M12 36H52" stroke="currentColor" strokeWidth="2.5" strokeDasharray="3 3" />
                    </svg>
                  </div>
                  <span className="deck-card-label">System Match</span>
                </div>
              </div>
            </div>

            {/* Section 2: Branding Hue Capsules */}
            <div className="customizer-hub-section">
              <span className="section-meta-tag">02 / Branding</span>
              <h3>Accent Palette</h3>
              <div className="capsule-palette-row">
                {[
                  { key: "red", color: "#dc2626", name: "Crimson Red" },
                  { key: "indigo", color: "#4f46e5", name: "Royal Indigo" },
                  { key: "violet", color: "#8b5cf6", name: "Electric Violet" },
                  { key: "teal", color: "#0d9488", name: "Deep Teal" }
                ].map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={`hue-capsule-btn ${accent === item.key ? "active" : ""}`}
                    onClick={() => {
                      setAccent(item.key);
                      triggerToast(`Branding hue changed to ${item.name}`);
                    }}
                    style={{ "--capsule-theme-color": item.color }}
                  >
                    <span className="capsule-indicator-dot" style={{ backgroundColor: item.color }}></span>
                    <span className="capsule-label-text">{item.name}</span>
                    {accent === item.key && <span className="capsule-active-check">✓</span>}
                  </button>
                ))}
              </div>
            </div>

            {/* Section 3: Typography Specimen Book */}
            <div className="customizer-hub-section">
              <span className="section-meta-tag">03 / Typography</span>
              <h3>Typeface Specimen</h3>

              <div className="specimen-book-grid">
                {/* Google Sans Flex Specimen */}
                <div
                  className={`specimen-card font-gs ${font === "google-sans-flex" ? "active" : ""}`}
                  onClick={() => {
                    setFont("google-sans-flex");
                    triggerToast("Typeface set to Google Sans Flex");
                  }}
                >
                  <div className="specimen-bg-glyph">G</div>
                  <div className="specimen-header">
                    <span className="specimen-title">Google Sans Flex</span>
                    <span className="specimen-badge">Variable font</span>
                  </div>
                  <div className="specimen-body">
                    <div className="specimen-char-line">Aa Bb Cc Dd Ee Ff Gg Hh</div>
                    <div className="specimen-numbers-line">0 1 2 3 4 5 6 7 8 9</div>
                    <p className="specimen-sample-paragraph">
                      Friendly geometric sans-serif featuring responsive variable widths and weights for crisp data rendering.
                    </p>
                  </div>

                  <div className="specimen-footer">
                    <span className="specimen-status-text">
                      {font === "google-sans-flex" ? "Active interface font" : "Click to apply"}
                    </span>
                  </div>
                </div>

                {/* Inter Specimen */}
                <div
                  className={`specimen-card font-inter ${font === "inter" ? "active" : ""}`}
                  onClick={() => {
                    setFont("inter");
                    triggerToast("Typeface set to Inter");
                  }}
                >
                  <div className="specimen-bg-glyph">I</div>
                  <div className="specimen-header">
                    <span className="specimen-title">Inter Typeface</span>
                    <span className="specimen-badge">Neo-Grotesque</span>
                  </div>
                  <div className="specimen-body">
                    <div className="specimen-char-line">Aa Bb Cc Dd Ee Ff Gg Hh</div>
                    <div className="specimen-numbers-line">0 1 2 3 4 5 6 7 8 9</div>
                    <p className="specimen-sample-paragraph">
                      A highly legible, clean neo-grotesque sans-serif designed meticulously for micro-typography and dense UI screens.
                    </p>
                  </div>

                  <div className="specimen-footer">
                    <span className="specimen-status-text">
                      {font === "inter" ? "Active interface font" : "Click to apply"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
}

export default ConfigurationPage;
